import { useState } from 'react';
import { toast } from 'react-toastify';
import { Save, X } from 'lucide-react';
import { Button, Modal } from '@components/ui';
import { UNIT_TYPES } from '@mocks/malzemeler';

export default function MaterialModal({ isOpen, onClose, onSubmit }) {
    const [formData, setFormData] = useState({
        name: '',
        unit: 'adet',
        totalRequired: '',
        contractor: '',
        startDate: '',
        endDate: '',
        totalAgreed: '',
        paymentSchedule: 'monthly',
        notes: '',
    });

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Lütfen malzeme adı girin');
            return;
        }
        if (!formData.totalRequired) {
            toast.error('Lütfen toplam ihtiyaç miktarı girin');
            return;
        }

        const newMaterial = {
            key: formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
            label: formData.name,
            color: 'bg-primary',
            data: {
                name: formData.name,
                unit: formData.unit,
                totalRequired: parseInt(formData.totalRequired) || 0,
                totalDelivered: 0,
                progressPercent: 0,
                deliveries: [],
                iscilik: {
                    contractor: formData.contractor,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    status: 'pending',
                    progressPercent: 0,
                    progressNotes: [],
                },
                payment: {
                    totalAgreed: parseInt(formData.totalAgreed) || 0,
                    totalPaid: 0,
                    remaining: parseInt(formData.totalAgreed) || 0,
                    paymentPlan: [],
                },
            },
        };

        onSubmit(newMaterial);
        toast.success(`${formData.name} başarıyla eklendi`);
        onClose();

        // Form sıfırla
        setFormData({
            name: '',
            unit: 'adet',
            totalRequired: '',
            contractor: '',
            startDate: '',
            endDate: '',
            totalAgreed: '',
            paymentSchedule: 'monthly',
            notes: '',
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            title="Yeni Malzeme Ekle"
            footer={
                <>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        className="w-full sm:w-auto min-h-[48px]"
                    >
                        İptal
                    </Button>
                    <Button
                        type="submit"
                        form="material-form"
                        variant="primary"
                        icon={<Save size={18} />}
                        className="w-full sm:w-auto min-h-[48px]"
                    >
                        Kaydet
                    </Button>
                </>
            }
        >
            <form id="material-form" onSubmit={handleSubmit} className="space-y-6">
                {/* Temel Bilgiler */}
                <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Temel Bilgiler</h4>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Malzeme Adı */}
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Malzeme Adı *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="Örn: Alüminyum Doğrama"
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-base focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* Birim */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">Birim</label>
                            <select
                                value={formData.unit}
                                onChange={(e) => handleChange('unit', e.target.value)}
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary"
                            >
                                {Object.entries(UNIT_TYPES).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Miktar */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Toplam İhtiyaç *
                        </label>
                        <input
                            type="number"
                            required
                            min="1"
                            value={formData.totalRequired}
                            onChange={(e) => handleChange('totalRequired', e.target.value)}
                            placeholder="0"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-base focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Yüklenici Bilgileri */}
                <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Yüklenici Bilgileri</h4>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Yüklenici / Firma</label>
                        <input
                            type="text"
                            value={formData.contractor}
                            onChange={(e) => handleChange('contractor', e.target.value)}
                            placeholder="Firma veya usta adı"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">Başlangıç Tarihi</label>
                            <input
                                type="date"
                                value={formData.startDate}
                                onChange={(e) => handleChange('startDate', e.target.value)}
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">Tahmini Bitiş Tarihi</label>
                            <input
                                type="date"
                                value={formData.endDate}
                                onChange={(e) => handleChange('endDate', e.target.value)}
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary"
                            />
                        </div>
                    </div>
                </div>

                {/* Ödeme Bilgileri */}
                <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Ödeme Bilgileri</h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">Anlaşılan Toplam Tutar (₺)</label>
                            <input
                                type="number"
                                min="0"
                                value={formData.totalAgreed}
                                onChange={(e) => handleChange('totalAgreed', e.target.value)}
                                placeholder="0"
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">Ödeme Planı</label>
                            <select
                                value={formData.paymentSchedule}
                                onChange={(e) => handleChange('paymentSchedule', e.target.value)}
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary"
                            >
                                <option value="upfront">Peşin</option>
                                <option value="monthly">Aylık Taksit</option>
                                <option value="milestone">İş Bitiminde</option>
                                <option value="mixed">Karma (Peşin + Taksit)</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Notlar */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Notlar</label>
                    <textarea
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Ek notlar..."
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary resize-none focus:ring-2 focus:ring-primary"
                    />
                </div>
            </form>
        </Modal>
    );
}
